import type { HistoricalData } from '@/queries/historical.query'
import { formatAmount, formatDate } from '@vaultsfyi/common/utils'
import { useMemo } from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { Loader } from '../common/loader'
import { ApyChartTooltip } from './apyChartTooltip'
import { ChartBrush, ChartBrushOverlay } from './chartBrush'
import { ChartContainer } from './chartContainer'
import { ChartWatermark } from './chartWatermark'

const STACK_ID = 'stackId'

interface AreaChartProps {
  data: HistoricalData
  includeRewards: boolean
  className?: string
  showBrush?: boolean
}

export const ApyAreaChart = ({ data, includeRewards, className, showBrush = false }: AreaChartProps) => {
  const { hasRewards, hasIntrinsic, processedData } = useMemo(() => {
    const hasRewards = includeRewards && data.some((point) => point.reward > 0)
    const hasIntrinsic = data.some((point) => point.intrinsicApyBase !== null)

    const processedData: any[] = []
    let prev = null

    for (const current of data) {
      if (prev) {
        const needsInterpolation =
          prev.base !== 0 && current.base !== 0 && Math.sign(prev.base) !== Math.sign(current.base)

        if (needsInterpolation) {
          const ratio = Math.abs(prev.base) / (Math.abs(prev.base) + Math.abs(current.base))
          processedData.push({
            ...prev,
            timestamp: prev.timestamp + (current.timestamp - prev.timestamp) * ratio,
            base: 0,
            positiveApy: 0,
            negativeApy: 0,
          })
        } else if (prev.base !== 0 && current.base === 0) {
          processedData.push({
            ...current,
            timestamp: (prev.timestamp + current.timestamp) / 2,
            base: 0,
            positiveApy: 0,
            negativeApy: 0,
          })
        }
      }

      processedData.push({
        ...current,
        positiveApy: current.base >= 0 ? current.base : null,
        negativeApy: current.base < 0 ? current.base : null,
        intrinsicApy: (includeRewards ? current.intrinsicApyTotal : current.intrinsicApyBase) || null,
        reward: current.reward || null,
      })

      prev = current
    }

    return { hasRewards, hasIntrinsic, processedData }
  }, [data, includeRewards])

  const hasBrushData = processedData.length > 0

  if (data.length === 0) {
    return (
      <ChartContainer className={className}>
        <Loader />
      </ChartContainer>
    )
  }

  return (
    <ChartContainer className={className}>
      <ResponsiveContainer>
        <AreaChart data={processedData}>
          <defs>
            <linearGradient id="apyPositiveFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--chart-green))" stopOpacity={1} />
              <stop offset="100%" stopColor="rgb(var(--chart-green))" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="apyNegativeFill" x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor="rgb(var(--chart-red))" stopOpacity={1} />
              <stop offset="100%" stopColor="rgb(var(--chart-red))" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="apyIntrinsicFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--chart-cyan))" stopOpacity={1} />
              <stop offset="100%" stopColor="rgb(var(--chart-cyan))" stopOpacity={0.05} />
            </linearGradient>
            <linearGradient id="apyRewardsFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgb(var(--chart-violet))" stopOpacity={1} />
              <stop offset="100%" stopColor="rgb(var(--chart-violet))" stopOpacity={0.05} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey="timestamp"
            tickLine={false}
            tickMargin={12}
            type="number"
            interval={'preserveStartEnd'}
            domain={['dataMin', 'dataMax']}
            tickCount={7}
            tickFormatter={(timestamp) => formatDate({ timestamp })}
          />
          <YAxis
            width={48}
            tickMargin={0}
            tickLine={false}
            tickFormatter={(value) => `${formatAmount(value, { notation: 'compact' })}%`}
            interval="preserveStartEnd"
          />
          <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgb(var(--border))" />
          <Tooltip content={({ payload }) => <ApyChartTooltip data={payload?.[0]?.payload} />} />
          <Area
            dataKey="positiveApy"
            stackId={STACK_ID}
            type="monotone"
            isAnimationActive={false}
            stroke="rgb(var(--chart-green))"
            strokeOpacity={1}
            strokeWidth={2}
            fill="url(#apyPositiveFill)"
          />
          <Area
            dataKey="negativeApy"
            stackId={STACK_ID}
            type="monotone"
            isAnimationActive={false}
            stroke="rgb(var(--chart-red))"
            strokeOpacity={1}
            strokeWidth={2}
            fill="url(#apyNegativeFill)"
          />
          {hasIntrinsic && (
            <Area
              dataKey="intrinsicApy"
              stackId={STACK_ID}
              type="monotone"
              isAnimationActive={false}
              stroke="rgb(var(--chart-cyan))"
              strokeOpacity={1}
              strokeWidth={2}
              fill="url(#apyIntrinsicFill)"
            />
          )}
          {hasRewards && (
            <Area
              dataKey="reward"
              stackId={STACK_ID}
              type="monotone"
              isAnimationActive={false}
              stroke="rgb(var(--chart-violet))"
              strokeOpacity={1}
              strokeWidth={2}
              fill="url(#apyRewardsFill)"
            />
          )}
          <ChartWatermark />
          {showBrush && hasBrushData && (
            <ChartBrush data={processedData}>
              <AreaChart>
                <defs>
                  <linearGradient id="apyBrushFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(var(--chart-green))" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="rgb(var(--chart-green))" stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="apyNegativeBrushFill" x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor="rgb(var(--chart-red))" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="rgb(var(--chart-red))" stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="apyIntrinsicBrushFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(var(--chart-cyan))" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="rgb(var(--chart-cyan))" stopOpacity={0.04} />
                  </linearGradient>
                  <linearGradient id="apyRewardsBrushFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="rgb(var(--chart-violet))" stopOpacity={0.45} />
                    <stop offset="100%" stopColor="rgb(var(--chart-violet))" stopOpacity={0.04} />
                  </linearGradient>
                </defs>
                <YAxis hide domain={['auto', 'auto']} />
                <Area
                  dataKey="positiveApy"
                  stackId={STACK_ID}
                  type="monotone"
                  isAnimationActive={false}
                  stroke="rgb(var(--chart-green))"
                  strokeWidth={2}
                  fill="url(#apyBrushFill)"
                />
                <Area
                  dataKey="negativeApy"
                  stackId={STACK_ID}
                  type="monotone"
                  isAnimationActive={false}
                  stroke="rgb(var(--chart-red))"
                  strokeWidth={2}
                  fill="url(#apyNegativeBrushFill)"
                />
                {hasIntrinsic && (
                  <Area
                    dataKey="intrinsicApy"
                    stackId={STACK_ID}
                    type="monotone"
                    isAnimationActive={false}
                    stroke="rgb(var(--chart-cyan))"
                    strokeWidth={2}
                    fill="url(#apyIntrinsicBrushFill)"
                  />
                )}
                {hasRewards && (
                  <Area
                    dataKey="reward"
                    stackId={STACK_ID}
                    type="monotone"
                    isAnimationActive={false}
                    stroke="rgb(var(--chart-violet))"
                    strokeWidth={2}
                    fill="url(#apyRewardsBrushFill)"
                  />
                )}
                <ChartBrushOverlay />
              </AreaChart>
            </ChartBrush>
          )}
        </AreaChart>
      </ResponsiveContainer>
    </ChartContainer>
  )
}
